import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { TipTapEditor } from '../../components/admin/TipTapEditor';
import { TaxonomySelect } from '../../components/admin/TaxonomySelect';
import { SlugField } from '../../components/admin/SlugField';
import { SEOFields } from '../../components/admin/SEOFields';
import { ReviewNeededBadge, fieldNeedsReview, mapTransformerFieldToFormField } from '../../components/admin/ReviewNeededBadge';
import { API_BASE } from '../../config/api';

export const ProgramForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [universities, setUniversities] = useState([]);
  const [originalSlug, setOriginalSlug] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [generalError, setGeneralError] = useState(null);
  const [missingFields, setMissingFields] = useState([]); // Track fields that need review from _metadata
  const [taxonomies, setTaxonomies] = useState({
    level_of_study: [],
    mode_of_study: [],
    field_of_study: [],
    subject: [],
    intake: [],
  });

  // AI Generation state
  const [aiReferenceUrls, setAiReferenceUrls] = useState(['']);
  const [aiUploadedFiles, setAiUploadedFiles] = useState([]);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiSuccess, setAiSuccess] = useState(false);

  const [formData, setFormData] = useState({
    university_id: '',
    program_name: '',
    program_code: '',
    slug: '',
    seo_title: '',
    seo_description: '',
    short_description: '',
    levels_of_study: [], // Multi-select array (canonical)
    fields_of_study: [], // Multi-select array (canonical)
    subject_tags: [], // Renamed from subject_major
    modes_of_study: [], // Multi-select array (canonical)
    duration: '',
    intake_months: [],
    // Fees with currency
    estimated_fee_local: '',
    fee_currency_local: 'MYR',
    estimated_fee_international: '',
    fee_currency_international: 'MYR',
    cashback_amount: '',
    unisave_commission_local: '',
    unisave_commission_international: '',
    apel_entry: '',
    // Eligibility
    eligibility_requirements: [{ title: '', description: '' }],
    english_requirements: { ielts: '', toefl: '', muet: '', other: '' },
    // Content
    learning_outcomes: '',
    career_outcomes: '',
    course_modules: [{ name: '', credits: '', description: '' }],
    program_modules: { type: 'doc', content: [] },
    program_highlights: { type: 'doc', content: [] },
    // Additional fields
    entry_pathways: [],
    approval_difficulty: '',
    internal_priority_score: 0,
    // Ad Banners
    ad_banners: [{ image_url: '', target_url: '' }],
    // CTA Buttons
    cta_buttons: [
      { text: 'Explore with Advisor', url: '', type: 'primary' },
      { text: 'Book Consultation', url: '', type: 'secondary' }
    ],
    published: false
  });

  // Currency options
  const currencyOptions = [
    { value: 'MYR', label: 'MYR (RM)', symbol: 'RM' },
    { value: 'USD', label: 'USD ($)', symbol: '$' },
    { value: 'INR', label: 'INR (₹)', symbol: '₹' },
    { value: 'EUR', label: 'EUR (€)', symbol: '€' },
    { value: 'GBP', label: 'GBP (£)', symbol: '£' },
    { value: 'CNY', label: 'CNY (¥)', symbol: '¥' },
    { value: 'IDR', label: 'IDR (Rp)', symbol: 'Rp' },
    { value: 'AED', label: 'AED (د.إ)', symbol: 'د.إ' }
  ];

  const backendUrl = API_BASE;

  useEffect(() => {
    fetchUniversities();
    fetchTaxonomies();
    if (isEdit) {
      fetchProgram();
    }
  }, [id]);

  const fetchTaxonomies = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${backendUrl}/api/admin/master-taxonomy`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) {
        console.error(`Failed to fetch taxonomies: HTTP ${res.status}`);
        setTaxonomies({ level_of_study: [], mode_of_study: [], field_of_study: [], subject: [], intake: [] });
        return;
      }

      const data = await res.json();
      
      if (!data || typeof data !== 'object') {
        console.error('Invalid taxonomy API response format:', data);
        setTaxonomies({ level_of_study: [], mode_of_study: [], field_of_study: [], subject: [], intake: [] });
        return;
      }

      const grouped = {
        level_of_study: [],
        mode_of_study: [],
        field_of_study: [],
        subject: [],
        intake: [],
      };

      const items = Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : []);
      if (!Array.isArray(items)) {
        console.error('Taxonomy items is not an array:', items);
        setTaxonomies({ level_of_study: [], mode_of_study: [], field_of_study: [], subject: [], intake: [] });
        return;
      }
      
      items.forEach(item => {
        if (item && item.type && typeof item.type === 'string' && grouped.hasOwnProperty(item.type)) {
          if (item.slug && item.label) {
            grouped[item.type].push(item);
          } else {
            console.warn(`Skipping taxonomy item with missing slug/label:`, item);
          }
        }
      });

      setTaxonomies(grouped);
    } catch (err) {
      console.error('Failed to load taxonomies', err);
      setTaxonomies({ level_of_study: [], mode_of_study: [], field_of_study: [], subject: [], intake: [] });
    }
  };

  const fetchUniversities = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/universities`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setUniversities(data.universities || []);
      }
    } catch (err) {
      console.error('Failed to fetch universities:', err);
    }
  };

  const fetchProgram = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/programs/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        navigate('/admin/login');
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch program');

      const data = await response.json();
      const progData = data.program || data;
      setOriginalSlug(progData.slug || '');
      
      // Extract metadata for review warnings
      if (progData._metadata && Array.isArray(progData._metadata.missing_fields)) {
        // Map transformer field names to form field names
        const mappedMissingFields = progData._metadata.missing_fields.map(field => 
          mapTransformerFieldToFormField(field, 'program')
        );
        setMissingFields(mappedMissingFields);
      } else {
        setMissingFields([]);
      }
      
      // Normalize legacy fields for edit mode:
      // - level_of_study (string) -> levels_of_study (array)
      // - mode_of_study (string) -> modes_of_study (array)
      // - field_of_study_primary/secondary -> fields_of_study (array)
      // - subject_major -> subject_tags
      const normalizedData = {
        ...progData,
        slug: progData.slug || '',
        seo_title: progData.seo_title || '',
        seo_description: progData.seo_description || '',
        program_code: progData.program_code || '',
        short_description: progData.short_description || '',
        // Normalize levels_of_study: handle legacy string or array
        levels_of_study: Array.isArray(progData.levels_of_study) 
          ? progData.levels_of_study 
          : (progData.level_of_study ? [progData.level_of_study] : []),
        // Normalize modes_of_study: handle legacy string or array
        modes_of_study: Array.isArray(progData.modes_of_study) 
          ? progData.modes_of_study 
          : (progData.mode_of_study ? [progData.mode_of_study] : []),
        // Normalize fields_of_study: derive from primary/secondary if needed
        fields_of_study: Array.isArray(progData.fields_of_study) && progData.fields_of_study.length > 0
          ? progData.fields_of_study
          : (() => {
              const fields = [];
              if (progData.field_of_study_primary) fields.push(progData.field_of_study_primary);
              if (progData.field_of_study_secondary) fields.push(progData.field_of_study_secondary);
              return fields;
            })(),
        // Normalize subject_tags: handle legacy subject_major
        subject_tags: Array.isArray(progData.subject_tags) 
          ? progData.subject_tags 
          : (Array.isArray(progData.subject_major) ? progData.subject_major : []),
        intake_months: Array.isArray(progData.intake_months) ? progData.intake_months : [],
        estimated_fee_local: progData.estimated_fee_local || '',
        fee_currency_local: progData.fee_currency_local || 'MYR',
        estimated_fee_international: progData.estimated_fee_international || '',
        fee_currency_international: progData.fee_currency_international || 'MYR',
        cashback_amount: progData.cashback_amount || '',
        unisave_commission_local: progData.unisave_commission_local || '',
        unisave_commission_international: progData.unisave_commission_international || '',
        apel_entry: progData.apel_entry || '',
        learning_outcomes: progData.learning_outcomes || '',
        career_outcomes: progData.career_outcomes || '',
        entry_pathways: Array.isArray(progData.entry_pathways) ? progData.entry_pathways : [],
        approval_difficulty: progData.approval_difficulty || '',
        internal_priority_score: typeof progData.internal_priority_score === 'number' ? progData.internal_priority_score : 0,
        english_requirements: progData.english_requirements && typeof progData.english_requirements === 'object'
          ? { ielts: '', toefl: '', muet: '', other: '', ...progData.english_requirements }
          : { ielts: '', toefl: '', muet: '', other: '' },
        course_modules: progData.course_modules && progData.course_modules.length > 0
          ? progData.course_modules
          : [{ name: '', credits: '', description: '' }],
        eligibility_requirements: progData.eligibility_requirements && progData.eligibility_requirements.length > 0
          ? progData.eligibility_requirements
          : [{ title: '', description: '' }],
        ad_banners: progData.ad_banners && progData.ad_banners.length > 0
          ? progData.ad_banners
          : [{ image_url: '', target_url: '' }],
        program_modules: typeof progData.program_modules === 'string' 
          ? { type: 'doc', content: [] } 
          : (progData.program_modules && typeof progData.program_modules === 'object' && progData.program_modules.type === 'doc'
            ? progData.program_modules 
            : (progData.program_modules && progData.program_modules.blocks ? { type: 'doc', content: [] } : { type: 'doc', content: [] })),
        program_highlights: typeof progData.program_highlights === 'string' 
          ? { type: 'doc', content: [] } 
          : (progData.program_highlights && typeof progData.program_highlights === 'object' && progData.program_highlights.type === 'doc'
            ? progData.program_highlights 
            : (progData.program_highlights && progData.program_highlights.blocks ? { type: 'doc', content: [] } : { type: 'doc', content: [] })),
        cta_buttons: progData.cta_buttons && progData.cta_buttons.length > 0
          ? progData.cta_buttons
          : [
              { text: 'Explore with Advisor', url: '', type: 'primary' },
              { text: 'Book Consultation', url: '', type: 'secondary' }
            ]
      };
      
      setFormData(normalizedData);
    } catch (err) {
      alert('Failed to load program');
      navigate('/admin/programs');
    } finally {
      setLoading(false);
    }
  };

  // Convert taxonomy slugs to labels when taxonomies are loaded (for edit mode display)
  useEffect(() => {
    if (!isEdit) return;
    
    // Convert levels_of_study slugs to labels
    if (taxonomies.level_of_study.length > 0 && formData.levels_of_study && formData.levels_of_study.length > 0) {
      const taxList = taxonomies.level_of_study || [];
      const firstValue = formData.levels_of_study[0];
      const isSlug = taxList.some(t => t.slug === firstValue) && !taxList.some(t => t.label === firstValue);
      
      if (isSlug) {
        const labels = formData.levels_of_study.map(slug => {
          const foundTax = taxList.find(t => t.slug === slug);
          return foundTax ? foundTax.label : slug;
        }).filter(Boolean);
        if (labels.length > 0) {
          setFormData(prev => ({...prev, levels_of_study: labels}));
        }
      }
    }
    
    // Convert modes_of_study slugs to labels
    if (taxonomies.mode_of_study.length > 0 && formData.modes_of_study && formData.modes_of_study.length > 0) {
      const taxList = taxonomies.mode_of_study || [];
      const firstValue = formData.modes_of_study[0];
      const isSlug = taxList.some(t => t.slug === firstValue) && !taxList.some(t => t.label === firstValue);
      
      if (isSlug) {
        const labels = formData.modes_of_study.map(slug => {
          const foundTax = taxList.find(t => t.slug === slug);
          return foundTax ? foundTax.label : slug;
        }).filter(Boolean);
        if (labels.length > 0) {
          setFormData(prev => ({...prev, modes_of_study: labels}));
        }
      }
    }
    
    // Convert fields_of_study slugs to labels
    if (taxonomies.field_of_study.length > 0 && formData.fields_of_study && formData.fields_of_study.length > 0) {
      const taxList = taxonomies.field_of_study || [];
      const firstValue = formData.fields_of_study[0];
      const isSlug = taxList.some(t => t.slug === firstValue) && !taxList.some(t => t.label === firstValue);
      
      if (isSlug) {
        const labels = formData.fields_of_study.map(slug => {
          const foundTax = taxList.find(t => t.slug === slug);
          return foundTax ? foundTax.label : slug;
        }).filter(Boolean);
        if (labels.length > 0) {
          setFormData(prev => ({...prev, fields_of_study: labels}));
        }
      }
    }
    
    // Convert subject_tags slugs to labels
    if (taxonomies.subject.length > 0 && formData.subject_tags && formData.subject_tags.length > 0) {
      const taxList = taxonomies.subject || [];
      const firstValue = formData.subject_tags[0];
      const isSlug = taxList.some(t => t.slug === firstValue) && !taxList.some(t => t.label === firstValue);
      
      if (isSlug) {
        const labels = formData.subject_tags.map(slug => {
          const foundTax = taxList.find(t => t.slug === slug);
          return foundTax ? foundTax.label : slug;
        }).filter(Boolean);
        if (labels.length > 0) {
          setFormData(prev => ({...prev, subject_tags: labels}));
        }
      }
    }
    
    // Convert intake_months slugs to labels
    if (taxonomies.intake.length > 0 && formData.intake_months && formData.intake_months.length > 0) {
      const taxList = taxonomies.intake || [];
      const firstValue = formData.intake_months[0];
      const isSlug = taxList.some(t => t.slug === firstValue) && !taxList.some(t => t.label === firstValue);
      
      if (isSlug) {
        const labels = formData.intake_months.map(slug => {
          const foundTax = taxList.find(t => t.slug === slug);
          return foundTax ? foundTax.label : slug;
        }).filter(Boolean);
        if (labels.length > 0) {
          setFormData(prev => ({...prev, intake_months: labels}));
        }
      }
    }
  }, [taxonomies.level_of_study.length, taxonomies.mode_of_study.length, taxonomies.field_of_study.length, taxonomies.subject.length, taxonomies.intake.length, isEdit]);

  // Helper: Convert taxonomy labels to slugs (for arrays)
  const convertLabelsToSlugs = (labels, taxonomyType) => {
    if (!Array.isArray(labels) || labels.length === 0) return [];
    const taxList = Array.isArray(taxonomies?.[taxonomyType]) ? (taxonomies[taxonomyType] || []) : [];
    const slugs = labels.map(label => {
      const foundTax = taxList.find(t => t.label === label || t.slug === label);
      return foundTax ? foundTax.slug : label; // Fallback to label if not found (will be validated by backend)
    }).filter(slug => slug); // Remove empty slugs
    return slugs;
  };

  // AI Generation Handler
  const handleAIGenerate = async () => {
    // Validate: at least one URL OR one file
    const validUrls = aiReferenceUrls.filter(url => url && typeof url === 'string' && url.trim() !== '');
    if (validUrls.length === 0 && aiUploadedFiles.length === 0) {
      setAiError('Please provide at least one reference URL or upload a file');
      return;
    }

    setAiGenerating(true);
    setAiError(null);
    setAiSuccess(false);

    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const response = await fetch(`${backendUrl}/api/admin/ai/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          entity_type: 'program',
          entity_id: id || undefined,
          reference_urls: validUrls,
          uploaded_files: aiUploadedFiles.map(f => f.file_id),
          tone: 'informative',
          target_audience: 'Prospective students and their families',
          seo_keywords: []
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'AI generation failed' }));
        throw new Error(errorData.detail || `AI generation failed (${response.status})`);
      }

      const result = await response.json();
      
      if (!result.success || !result.content) {
        throw new Error(result.message || 'AI generation returned invalid response');
      }

      // Map AI response to form fields
      const aiContent = result.content;
      
      // Check for existing form data (warn if fields are already filled)
      const hasExistingData = Object.values(formData).some(value => {
        if (Array.isArray(value)) return value.length > 0 && (value[0] !== '' && value[0] !== null);
        if (typeof value === 'object' && value !== null) {
          if (value.blocks && Array.isArray(value.blocks)) return value.blocks.length > 0;
          return Object.keys(value).length > 0;
        }
        return value !== '' && value !== null && value !== 0 && value !== false;
      });

      if (hasExistingData && !window.confirm('This will populate form fields with AI-generated content. Existing data may be overwritten. Continue?')) {
        setAiGenerating(false);
        return;
      }

      // Helper: Convert HTML string to TipTap JSON format
      const htmlToTipTap = (html) => {
        if (!html || typeof html !== 'string' || html.trim() === '') return { type: 'doc', content: [] };
        try {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = html;
          const textContent = tempDiv.textContent || tempDiv.innerText || '';
          if (!textContent || typeof textContent !== 'string' || textContent.trim() === '') return { type: 'doc', content: [] };
          const paragraphs = textContent.split(/\n\s*\n/).filter(p => p && typeof p === 'string' && p.trim());
          if (paragraphs.length === 0) {
            return {
              type: 'doc',
              content: [{ type: 'paragraph', content: [{ type: 'text', text: (textContent && typeof textContent === 'string') ? textContent.trim() : '' }] }]
            };
          }
          return {
            type: 'doc',
            content: paragraphs.map(para => ({
              type: 'paragraph',
              content: [{ type: 'text', text: (para && typeof para === 'string') ? para.trim() : '' }]
            }))
          };
        } catch (e) {
          const plainText = (html && typeof html === 'string') ? html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() : '';
          return {
            type: 'doc',
            content: [{ type: 'paragraph', content: [{ type: 'text', text: plainText }] }]
          };
        }
      };

      // Map fields from AI response
      const updatedFormData = { ...formData };

      // Basic fields
      if (aiContent.program_name) updatedFormData.program_name = aiContent.program_name;
      if (aiContent.program_code) updatedFormData.program_code = aiContent.program_code;
      if (aiContent.short_description) updatedFormData.short_description = aiContent.short_description;
      if (aiContent.duration) updatedFormData.duration = aiContent.duration;
      if (aiContent.learning_outcomes) updatedFormData.learning_outcomes = aiContent.learning_outcomes;
      if (aiContent.career_outcomes) updatedFormData.career_outcomes = aiContent.career_outcomes;
      if (aiContent.approval_difficulty) updatedFormData.approval_difficulty = aiContent.approval_difficulty;

      // SEO fields
      if (aiContent.seo_title) updatedFormData.seo_title = aiContent.seo_title;
      if (aiContent.seo_description) updatedFormData.seo_description = aiContent.seo_description;

      // Fees
      if (aiContent.estimated_fee_local !== undefined && aiContent.estimated_fee_local !== null) {
        updatedFormData.estimated_fee_local = String(aiContent.estimated_fee_local);
      }
      if (aiContent.fee_currency_local) updatedFormData.fee_currency_local = aiContent.fee_currency_local;
      if (aiContent.estimated_fee_international !== undefined && aiContent.estimated_fee_international !== null) {
        updatedFormData.estimated_fee_international = String(aiContent.estimated_fee_international);
      }
      if (aiContent.fee_currency_international) updatedFormData.fee_currency_international = aiContent.fee_currency_international;
      if (aiContent.cashback_amount !== undefined && aiContent.cashback_amount !== null) {
        updatedFormData.cashback_amount = String(aiContent.cashback_amount);
      }

      // Arrays (taxonomy fields - AI returns slugs, convert to labels for UI)
      if (Array.isArray(aiContent.levels_of_study) && aiContent.levels_of_study.length > 0) {
        const labels = aiContent.levels_of_study.map(slug => {
          const foundTax = taxonomies.level_of_study.find(t => t.slug === slug);
          return foundTax ? foundTax.label : slug;
        });
        updatedFormData.levels_of_study = labels;
      }
      if (Array.isArray(aiContent.modes_of_study) && aiContent.modes_of_study.length > 0) {
        const labels = aiContent.modes_of_study.map(slug => {
          const foundTax = taxonomies.mode_of_study.find(t => t.slug === slug);
          return foundTax ? foundTax.label : slug;
        });
        updatedFormData.modes_of_study = labels;
      }
      if (Array.isArray(aiContent.fields_of_study) && aiContent.fields_of_study.length > 0) {
        const labels = aiContent.fields_of_study.map(slug => {
          const foundTax = taxonomies.field_of_study.find(t => t.slug === slug);
          return foundTax ? foundTax.label : slug;
        });
        updatedFormData.fields_of_study = labels;
      }
      if (Array.isArray(aiContent.subject_tags) && aiContent.subject_tags.length > 0) {
        const labels = aiContent.subject_tags.map(slug => {
          const foundTax = taxonomies.subject.find(t => t.slug === slug);
          return foundTax ? foundTax.label : slug;
        });
        updatedFormData.subject_tags = labels;
      }
      if (Array.isArray(aiContent.intake_months) && aiContent.intake_months.length > 0) {
        const labels = aiContent.intake_months.map(slug => {
          const foundTax = taxonomies.intake.find(t => t.slug === slug);
          return foundTax ? foundTax.label : slug;
        });
        updatedFormData.intake_months = labels;
      }
      if (Array.isArray(aiContent.entry_pathways)) {
        updatedFormData.entry_pathways = aiContent.entry_pathways;
      }

      // TipTap fields (program_modules, program_highlights)
      if (aiContent.program_modules) {
        if (typeof aiContent.program_modules === 'string') {
          updatedFormData.program_modules = htmlToTipTap(aiContent.program_modules);
        } else if (typeof aiContent.program_modules === 'object') {
          updatedFormData.program_modules = aiContent.program_modules;
        }
      }
      if (aiContent.program_highlights) {
        if (typeof aiContent.program_highlights === 'string') {
          updatedFormData.program_highlights = htmlToTipTap(aiContent.program_highlights);
        } else if (typeof aiContent.program_highlights === 'object') {
          updatedFormData.program_highlights = aiContent.program_highlights;
        }
      }

      // Complex objects
      if (Array.isArray(aiContent.eligibility_requirements)) {
        updatedFormData.eligibility_requirements = aiContent.eligibility_requirements;
      }
      if (typeof aiContent.english_requirements === 'object' && aiContent.english_requirements !== null) {
        updatedFormData.english_requirements = { ...updatedFormData.english_requirements, ...aiContent.english_requirements };
      }
      if (Array.isArray(aiContent.course_modules)) {
        updatedFormData.course_modules = aiContent.course_modules;
      }

      setFormData(updatedFormData);
      setAiSuccess(true);
      setTimeout(() => setAiSuccess(false), 5000);

    } catch (err) {
      console.error('AI generation error:', err);
      setAiError(err.message || 'Failed to generate content with AI');
    } finally {
      setAiGenerating(false);
    }
  };

  // Helper: Add/remove reference URLs
  const addReferenceUrl = () => {
    setAiReferenceUrls([...aiReferenceUrls, '']);
  };

  const removeReferenceUrl = (index) => {
    if (aiReferenceUrls.length > 1) {
      setAiReferenceUrls(aiReferenceUrls.filter((_, i) => i !== index));
    }
  };

  const updateReferenceUrl = (index, value) => {
    const updated = [...aiReferenceUrls];
    updated[index] = value;
    setAiReferenceUrls(updated);
  };

  // File upload handlers
  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const token = localStorage.getItem('admin_token');
    if (!token) {
      setAiError('Please log in first');
      return;
    }

    const validFiles = files.filter(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      return ['pdf', 'docx', 'doc'].includes(ext);
    });

    if (validFiles.length !== files.length) {
      setAiError('Only PDF, DOCX, and DOC files are allowed');
      return;
    }

    setAiError(null);
    const uploadedFileIds = [];

    for (const file of validFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${backendUrl}/api/admin/ai/upload-file`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ detail: 'Upload failed' }));
          throw new Error(errorData.detail || `Failed to upload ${file.name}`);
        }

        const result = await response.json();
        if (result.success && result.file_id) {
          uploadedFileIds.push({
            file_id: result.file_id,
            filename: result.filename || file.name,
            size_mb: result.size_mb
          });
        }
      } catch (err) {
        setAiError(`Failed to upload ${file.name}: ${err.message}`);
        return;
      }
    }

    if (uploadedFileIds.length > 0) {
      setAiUploadedFiles(prev => [...prev, ...uploadedFileIds]);
    }

    // Reset file input
    event.target.value = '';
  };

  const removeUploadedFile = (index) => {
    setAiUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('admin_token');
      const url = isEdit
        ? `${backendUrl}/api/admin/programs/${id}`
        : `${backendUrl}/api/admin/programs`;

      // Prepare submit data with taxonomy label→slug conversion
      // Normalize string fields to prevent undefined trim() errors
      const submitData = {
        ...formData,
        // Ensure all string fields are strings or empty strings (prevents undefined.trim() errors)
        program_name: (formData.program_name && typeof formData.program_name === 'string') ? formData.program_name : (formData.program_name || ''),
        program_code: (formData.program_code && typeof formData.program_code === 'string') ? formData.program_code : (formData.program_code || ''),
        slug: (formData.slug && typeof formData.slug === 'string') ? formData.slug : (formData.slug || ''),
        seo_title: (formData.seo_title && typeof formData.seo_title === 'string') ? formData.seo_title : (formData.seo_title || ''),
        seo_description: (formData.seo_description && typeof formData.seo_description === 'string') ? formData.seo_description : (formData.seo_description || ''),
        short_description: (formData.short_description && typeof formData.short_description === 'string') ? formData.short_description : (formData.short_description || ''),
        duration: (formData.duration && typeof formData.duration === 'string') ? formData.duration : (formData.duration || ''),
        apel_entry: (formData.apel_entry && typeof formData.apel_entry === 'string') ? formData.apel_entry : (formData.apel_entry || ''),
        learning_outcomes: (formData.learning_outcomes && typeof formData.learning_outcomes === 'string') ? formData.learning_outcomes : (formData.learning_outcomes || ''),
        career_outcomes: (formData.career_outcomes && typeof formData.career_outcomes === 'string') ? formData.career_outcomes : (formData.career_outcomes || ''),
        approval_difficulty: (formData.approval_difficulty && typeof formData.approval_difficulty === 'string') ? formData.approval_difficulty : (formData.approval_difficulty || ''),
        // Convert taxonomy arrays from labels to slugs
        levels_of_study: convertLabelsToSlugs(formData.levels_of_study || [], 'level_of_study'),
        modes_of_study: convertLabelsToSlugs(formData.modes_of_study || [], 'mode_of_study'),
        fields_of_study: convertLabelsToSlugs(formData.fields_of_study || [], 'field_of_study'),
        subject_tags: convertLabelsToSlugs(formData.subject_tags || [], 'subject'),
        intake_months: convertLabelsToSlugs(formData.intake_months || [], 'intake'),
        // Type conversions
        estimated_fee_local: formData.estimated_fee_local ? parseFloat(formData.estimated_fee_local) : null,
        estimated_fee_international: formData.estimated_fee_international ? parseFloat(formData.estimated_fee_international) : null,
        fee_currency_local: formData.fee_currency_local || 'MYR',
        fee_currency_international: formData.fee_currency_international || 'MYR',
        cashback_amount: formData.cashback_amount ? parseFloat(formData.cashback_amount) : null,
        internal_priority_score: typeof formData.internal_priority_score === 'number' ? formData.internal_priority_score : parseInt(formData.internal_priority_score) || 0,
        // Filter empty values - ensure arrays exist before filtering
        eligibility_requirements: Array.isArray(formData.eligibility_requirements) ? formData.eligibility_requirements.filter(
          req => req && ((req.title && typeof req.title === 'string' && req.title.trim() !== '') || (req.description && typeof req.description === 'string' && req.description.trim() !== ''))
        ) : [],
        course_modules: Array.isArray(formData.course_modules) ? formData.course_modules.filter(
          mod => mod && mod.name && typeof mod.name === 'string' && mod.name.trim() !== ''
        ) : [],
        ad_banners: Array.isArray(formData.ad_banners) ? formData.ad_banners.filter(
          banner => banner && banner.image_url && typeof banner.image_url === 'string' && banner.image_url.trim() !== ''
        ) : [],
        entry_pathways: Array.isArray(formData.entry_pathways) ? formData.entry_pathways.filter(path => path && typeof path === 'string' && path.trim() !== '') : []
      };

      // Remove legacy fields that shouldn't be sent
      delete submitData.level_of_study;
      delete submitData.mode_of_study;
      delete submitData.field_of_study;
      delete submitData.subject_major;

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to save program' }));
        
        // Parse error response
        const errors = {};
        let generalError = null;
        
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            // FastAPI validation errors format: [{ loc: ["body", "field"], msg: "message" }, ...]
            errorData.detail.forEach(err => {
              if (err.loc && Array.isArray(err.loc)) {
                const field = err.loc[err.loc.length - 1]; // Get the last element (field name)
                errors[field] = err.msg || 'Invalid value';
              } else {
                generalError = err.msg || 'Validation error';
              }
            });
          } else if (typeof errorData.detail === 'string') {
            // Simple string error (may contain multiple errors separated by semicolons)
            const errorMessages = (errorData.detail && typeof errorData.detail === 'string') ? errorData.detail.split(';').map(e => e && typeof e === 'string' ? e.trim() : '').filter(e => e) : [];
            
            // Try to extract field names from error messages
            errorMessages.forEach(msg => {
              const fieldMatch = msg.match(/(\w+):\s*(.+)/i);
              if (fieldMatch) {
                const field = fieldMatch[1];
                errors[field] = fieldMatch[2];
              } else {
                // Check for common field patterns
                const commonFields = ['university_id', 'program_name', 'levels_of_study', 'modes_of_study', 'fields_of_study', 'estimated_fee_local', 'estimated_fee_international'];
                const matchedField = commonFields.find(f => msg.toLowerCase().includes(f.replace(/_/g, ' ')));
                if (matchedField) {
                  errors[matchedField] = msg;
                } else {
                  if (!generalError) generalError = msg;
                  else generalError += '; ' + msg;
                }
              }
            });
          }
        } else {
          generalError = 'Failed to save program';
        }
        
        setFieldErrors(errors);
        setGeneralError(generalError);
        
        // Scroll to top to show errors
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        return; // Don't navigate on error
      }

      // Clear errors on success
      setFieldErrors({});
      setGeneralError(null);
      navigate('/admin/programs');
    } catch (err) {
      // Network errors or other exceptions
      const errorMessage = err.message || 'Failed to save program';
      setGeneralError(errorMessage);
      setFieldErrors({});
      console.error('Error saving program:', err);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  // Eligibility Requirements handlers
  const addEligibilityRequirement = () => {
    setFormData(prev => ({
      ...prev,
      eligibility_requirements: [...prev.eligibility_requirements, { title: '', description: '' }]
    }));
  };

  const removeEligibilityRequirement = (index) => {
    setFormData(prev => ({
      ...prev,
      eligibility_requirements: prev.eligibility_requirements.filter((_, i) => i !== index)
    }));
  };

  const updateEligibilityRequirement = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      eligibility_requirements: prev.eligibility_requirements.map((req, i) =>
        i === index ? { ...req, [field]: value } : req
      )
    }));
  };

  // Course Modules handlers
  const addCourseModule = () => {
    setFormData(prev => ({
      ...prev,
      course_modules: [...prev.course_modules, { name: '', credits: '', description: '' }]
    }));
  };

  const removeCourseModule = (index) => {
    setFormData(prev => ({
      ...prev,
      course_modules: prev.course_modules.filter((_, i) => i !== index)
    }));
  };

  const updateCourseModule = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      course_modules: prev.course_modules.map((mod, i) =>
        i === index ? { ...mod, [field]: value } : mod
      )
    }));
  };

  // CTA Button handlers
  const updateCtaButton = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      cta_buttons: prev.cta_buttons.map((btn, i) =>
        i === index ? { ...btn, [field]: value } : btn
      )
    }));
  };

  // Ad Banner handlers
  const updateAdBanner = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      ad_banners: prev.ad_banners.map((banner, i) =>
        i === index ? { ...banner, [field]: value } : banner
      )
    }));
  };

  const addAdBanner = () => {
    setFormData(prev => ({
      ...prev,
      ad_banners: [...prev.ad_banners, { image_url: '', target_url: '' }]
    }));
  };

  const removeAdBanner = (index) => {
    setFormData(prev => ({
      ...prev,
      ad_banners: prev.ad_banners.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block w-12 h-12 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-[#6B7280]">Loading program...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-4xl">
        {/* Header */}
        <div>
          <button
            onClick={() => navigate('/admin/programs')}
            className="text-[#1E40AF] hover:text-[#1e3a8a] font-medium text-sm flex items-center gap-2 mb-3"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Programs
          </button>
          <h1 className="text-2xl font-bold text-[#111827]">{isEdit ? 'Edit' : 'Add'} Program</h1>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error Display */}
          {(generalError || Object.keys(fieldErrors).length > 0) && (
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-red-800 mb-2">Please fix the following errors:</h3>
                  {generalError && (
                    <p className="text-sm text-red-700 mb-2">{generalError}</p>
                  )}
                  {Object.keys(fieldErrors).length > 0 && (
                    <ul className="list-disc list-inside space-y-1">
                      {Object.entries(fieldErrors).map(([field, error]) => (
                        <li key={field} className="text-sm text-red-700">
                          <span className="font-medium">{field.replace(/_/g, ' ')}:</span> {error}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* AI Content Generation */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-[#1E40AF] mb-2">✨ AI Content Generation</h2>
                <p className="text-sm text-[#6B7280]">
                  Provide reference URLs or upload PDF/DOCX brochures to automatically generate program details. You may provide URLs or files, or both. AI-generated content must be reviewed before publishing.
                </p>
              </div>
            </div>

            {/* Reference URLs Input */}
            <div className="space-y-3 mb-4">
              <label className="block text-sm font-medium text-[#374151]">
                Reference URLs (official program pages, brochures, etc.)
              </label>
              {aiReferenceUrls.map((url, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="url"
                    value={url}
                    onChange={(e) => updateReferenceUrl(index, e.target.value)}
                    placeholder="https://university.edu/programs/program-name"
                    className="flex-1 px-4 py-2.5 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                    disabled={aiGenerating}
                  />
                  {aiReferenceUrls.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeReferenceUrl(index)}
                      className="px-3 py-2.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                      disabled={aiGenerating}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addReferenceUrl}
                className="text-sm text-[#1E40AF] hover:text-[#1e3a8a] font-medium flex items-center gap-1"
                disabled={aiGenerating}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add another URL
              </button>
            </div>

            {/* File Upload Input */}
            <div className="space-y-3 mb-4">
              <label className="block text-sm font-medium text-[#374151]">
                Upload Documents (PDF, DOCX) - Optional
              </label>
              <input
                type="file"
                accept=".pdf,.docx,.doc"
                multiple
                onChange={handleFileUpload}
                disabled={aiGenerating}
                className="block w-full text-sm text-[#6B7280] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#1E40AF] file:text-white hover:file:bg-[#1e3a8a] file:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              />
              {aiUploadedFiles.length > 0 && (
                <div className="space-y-2">
                  {aiUploadedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-white border border-[#E5E7EB] rounded-lg">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <svg className="w-5 h-5 text-[#6B7280] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm text-[#374151] truncate">{file.filename}</span>
                        <span className="text-xs text-[#6B7280] flex-shrink-0">({file.size_mb} MB)</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeUploadedFile(index)}
                        className="ml-2 p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                        disabled={aiGenerating}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Generate Button */}
            <button
              type="button"
              onClick={handleAIGenerate}
              disabled={aiGenerating || (aiReferenceUrls.filter(url => url && typeof url === 'string' && url.trim() !== '').length === 0 && aiUploadedFiles.length === 0)}
              className="w-full px-6 py-3 bg-gradient-to-r from-[#1E40AF] to-[#3B82F6] hover:from-[#1e3a8a] hover:to-[#2563EB] text-white font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {aiGenerating ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Generating content with AI...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span>Generate All Details with AI</span>
                </>
              )}
            </button>

            {/* Error Message */}
            {aiError && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{aiError}</p>
              </div>
            )}

            {/* Success Message */}
            {aiSuccess && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  ✅ Content generated successfully! Please review all fields before saving.
                </p>
              </div>
            )}

            {/* Disclaimer */}
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-800">
                <strong>⚠️ Important:</strong> AI-generated content must be reviewed and edited before publishing. 
                Verify all information, especially fees, eligibility requirements, and program details.
              </p>
            </div>
          </div>

          {/* Basic Information */}
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
            <h2 className="text-lg font-bold text-[#111827] mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#374151] mb-2">University *</label>
                <select
                  required
                  value={formData.university_id}
                  onChange={(e) => setFormData({...formData, university_id: e.target.value})}
                  className="w-full px-4 py-2.5 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                >
                  <option value="">Select University</option>
                  {universities.map(uni => (
                    <option key={uni.id} value={uni.id}>{uni.name}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#374151] mb-2">
                  Program Name *
                  <ReviewNeededBadge fieldName="program_name" missingFields={missingFields} />
                </label>
                <input
                  type="text"
                  required
                  value={formData.program_name}
                  onChange={(e) => setFormData({...formData, program_name: e.target.value})}
                  className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${
                    fieldNeedsReview('program_name', missingFields)
                      ? 'border-amber-300 focus:ring-amber-400 bg-amber-50'
                      : 'border-[#E5E7EB] focus:ring-[#1E40AF]'
                  }`}
                  placeholder="e.g., Bachelor of Computer Science"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">Program Code</label>
                <input
                  type="text"
                  value={formData.program_code || ''}
                  onChange={(e) => setFormData({...formData, program_code: e.target.value})}
                  className="w-full px-4 py-2.5 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                  placeholder="e.g., BCS-2024"
                />
                <p className="text-xs text-[#6B7280] mt-1">Unique identifier for this program</p>
              </div>

              <div className="md:col-span-2">
                <SlugField
                  value={formData.slug}
                  onChange={(slug) => setFormData({...formData, slug})}
                  sourceValue={formData.program_name}
                  isEdit={isEdit}
                  originalSlug={originalSlug}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#374151] mb-2">
                  Short Description
                  <ReviewNeededBadge fieldName="short_description" missingFields={missingFields} />
                </label>
                <textarea
                  value={formData.short_description}
                  onChange={(e) => setFormData({...formData, short_description: e.target.value})}
                  rows={3}
                  className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${
                    fieldNeedsReview('short_description', missingFields)
                      ? 'border-amber-300 focus:ring-amber-400 bg-amber-50'
                      : 'border-[#E5E7EB] focus:ring-[#1E40AF]'
                  }`}
                  placeholder="Brief description shown in the hero section (1-2 sentences)"
                />
                <p className="text-xs text-[#6B7280] mt-1">Displayed in the program hero section. Keep it concise.</p>
              </div>

              <div>
                <div className="flex items-center mb-2">
                  <label className="block text-sm font-medium text-[#374151]">
                    Levels of Study
                    <ReviewNeededBadge fieldName="levels_of_study" missingFields={missingFields} />
                  </label>
                </div>
                <TaxonomySelect
                  type="level_of_study"
                  label=""
                  value={formData.levels_of_study || []}
                  onChange={(values) => {
                    // TaxonomySelect with multiple=true returns array of labels
                    const labelArray = Array.isArray(values) ? values : [];
                    setFormData(prev => ({...prev, levels_of_study: labelArray}));
                  }}
                  multiple={true}
                  placeholder="Select levels..."
                />
              </div>

              <div>
                <div className="flex items-center mb-2">
                  <label className="block text-sm font-medium text-[#374151]">
                    Fields of Study
                    <ReviewNeededBadge fieldName="fields_of_study" missingFields={missingFields} />
                  </label>
                </div>
                <TaxonomySelect
                  type="field_of_study"
                  label=""
                  value={formData.fields_of_study || []}
                  onChange={(values) => {
                    // TaxonomySelect with multiple=true returns array of labels
                    const labelArray = Array.isArray(values) ? values : [];
                    setFormData(prev => ({...prev, fields_of_study: labelArray}));
                  }}
                  multiple={true}
                  placeholder="Select fields..."
                />
              </div>

              <div>
                <TaxonomySelect
                  type="subject"
                  label="Subject Tags"
                  value={formData.subject_tags || []}
                  onChange={(values) => {
                    // TaxonomySelect with multiple=true returns array of labels
                    const labelArray = Array.isArray(values) ? values : [];
                    setFormData(prev => ({...prev, subject_tags: labelArray}));
                  }}
                  multiple={true}
                  placeholder="Select subjects..."
                />
              </div>

              <div>
                <TaxonomySelect
                  type="mode_of_study"
                  label="Modes of Study"
                  value={formData.modes_of_study || []}
                  onChange={(values) => {
                    // TaxonomySelect with multiple=true returns array of labels
                    const labelArray = Array.isArray(values) ? values : [];
                    setFormData(prev => ({...prev, modes_of_study: labelArray}));
                  }}
                  multiple={true}
                  placeholder="Select modes..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">Duration</label>
                <input
                  type="text"
                  value={formData.duration}
                  onChange={(e) => setFormData({...formData, duration: e.target.value})}
                  className="w-full px-4 py-2.5 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                  placeholder="e.g., 3 years, 18 months"
                />
              </div>

              <div>
                <TaxonomySelect
                  type="intake"
                  label="Intake Months"
                  value={formData.intake_months || []}
                  onChange={(values) => {
                    // TaxonomySelect with multiple=true returns array of labels
                    const labelArray = Array.isArray(values) ? values : [];
                    setFormData(prev => ({...prev, intake_months: labelArray}));
                  }}
                  multiple={true}
                  placeholder="Select intakes..."
                />
              </div>
            </div>
          </div>

          {/* Program Content - About */}
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
            <h2 className="text-lg font-bold text-[#111827] mb-4">About the Program</h2>
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-2">Learning Outcomes / About Content</label>
              <textarea
                value={formData.learning_outcomes}
                onChange={(e) => setFormData({...formData, learning_outcomes: e.target.value})}
                rows={6}
                className="w-full px-4 py-2.5 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                placeholder="Describe what students will learn in this program, key outcomes, and benefits..."
              />
              <p className="text-xs text-[#6B7280] mt-1">This content appears in the "About the Program" section. Supports HTML.</p>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-[#374151] mb-2">Career Outcomes</label>
              <textarea
                value={formData.career_outcomes || ''}
                onChange={(e) => setFormData({...formData, career_outcomes: e.target.value})}
                rows={4}
                className="w-full px-4 py-2.5 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                placeholder="Describe career opportunities and paths after graduation (supports HTML)..."
              />
              <p className="text-xs text-[#6B7280] mt-1">Career prospects and opportunities for graduates.</p>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-[#374151] mb-2">Entry Pathways (One per line)</label>
              <textarea
                value={Array.isArray(formData.entry_pathways) ? formData.entry_pathways.join('\n') : ''}
                onChange={(e) => {
                  const lines = e.target.value.split('\n').map(line => line && typeof line === 'string' ? line.trim() : '').filter(line => line);
                  setFormData(prev => ({...prev, entry_pathways: lines}));
                }}
                rows={3}
                className="w-full px-4 py-2.5 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                placeholder="STPM&#10;A-Level&#10;Foundation&#10;Diploma"
              />
              <p className="text-xs text-[#6B7280] mt-1">List accepted entry qualifications, one per line.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">Approval Difficulty</label>
                <select
                  value={formData.approval_difficulty || ''}
                  onChange={(e) => setFormData({...formData, approval_difficulty: e.target.value})}
                  className="w-full px-4 py-2.5 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                >
                  <option value="">Select difficulty</option>
                  <option value="Easy">Easy</option>
                  <option value="Moderate">Moderate</option>
                  <option value="Strict">Strict</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">Internal Priority Score</label>
                <input
                  type="number"
                  value={formData.internal_priority_score || 0}
                  onChange={(e) => setFormData({...formData, internal_priority_score: parseInt(e.target.value) || 0})}
                  className="w-full px-4 py-2.5 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                  placeholder="0-100"
                  min="0"
                  max="100"
                />
                <p className="text-xs text-[#6B7280] mt-1">Internal admin priority ranking (0-100)</p>
              </div>
            </div>
          </div>

          {/* Course Modules */}
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-[#111827]">
                Course Structure / Modules
                <ReviewNeededBadge fieldName="course_modules" missingFields={missingFields} />
              </h2>
              <button
                type="button"
                onClick={addCourseModule}
                className="text-[#1E40AF] hover:text-[#1e3a8a] font-medium text-sm flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Module
              </button>
            </div>
            <div className="space-y-4">
              {formData.course_modules.map((mod, index) => (
                <div key={index} className="border border-[#E5E7EB] rounded-lg p-4 bg-[#F9FAFB]">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-sm font-semibold text-[#374151]">Module {index + 1}</h3>
                    {formData.course_modules.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeCourseModule(index)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="md:col-span-2">
                      <input
                        type="text"
                        value={mod.name}
                        onChange={(e) => updateCourseModule(index, 'name', e.target.value)}
                        className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent text-sm"
                        placeholder="Module name"
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        value={mod.credits}
                        onChange={(e) => updateCourseModule(index, 'credits', e.target.value)}
                        className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent text-sm"
                        placeholder="Credits"
                      />
                    </div>
                    <div className="md:col-span-4">
                      <textarea
                        value={mod.description}
                        onChange={(e) => updateCourseModule(index, 'description', e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent text-sm"
                        placeholder="Module description (optional)"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Fees */}
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
            <h2 className="text-lg font-bold text-[#111827] mb-4">Fees & Cost</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">
                  Estimated Fee (Local Students)
                  <ReviewNeededBadge fieldName="estimated_fee_local" missingFields={missingFields} />
                </label>
                <div className="flex gap-2">
                  <select
                    value={formData.fee_currency_local}
                    onChange={(e) => setFormData({...formData, fee_currency_local: e.target.value})}
                    className="px-3 py-2.5 bg-[#F3F4F6] border border-[#E5E7EB] rounded-lg text-sm text-[#374151] focus:outline-none focus:ring-2 focus:ring-[#1E40AF]"
                  >
                    {currencyOptions.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                  <input
                    type="number"
                    value={formData.estimated_fee_local}
                    onChange={(e) => setFormData({...formData, estimated_fee_local: e.target.value})}
                    className={`flex-1 px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${
                      fieldNeedsReview('estimated_fee_local', missingFields)
                        ? 'border-amber-300 focus:ring-amber-400 bg-amber-50'
                        : 'border-[#E5E7EB] focus:ring-[#1E40AF]'
                    }`}
                    placeholder="e.g., 45000"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">
                  Estimated Fee (International Students)
                  <ReviewNeededBadge fieldName="estimated_fee_international" missingFields={missingFields} />
                </label>
                <div className="flex gap-2">
                  <select
                    value={formData.fee_currency_international}
                    onChange={(e) => setFormData({...formData, fee_currency_international: e.target.value})}
                    className="px-3 py-2.5 bg-[#F3F4F6] border border-[#E5E7EB] rounded-lg text-sm text-[#374151] focus:outline-none focus:ring-2 focus:ring-[#1E40AF]"
                  >
                    {currencyOptions.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                  <input
                    type="number"
                    value={formData.estimated_fee_international}
                    onChange={(e) => setFormData({...formData, estimated_fee_international: e.target.value})}
                    className={`flex-1 px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${
                      fieldNeedsReview('estimated_fee_international', missingFields)
                        ? 'border-amber-300 focus:ring-amber-400 bg-amber-50'
                        : 'border-[#E5E7EB] focus:ring-[#1E40AF]'
                    }`}
                    placeholder="e.g., 55000"
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#374151] mb-2">Cashback Amount (Optional)</label>
                <div className="flex gap-2">
                  <span className="px-3 py-2.5 bg-[#F3F4F6] border border-[#E5E7EB] rounded-lg text-sm text-[#6B7280]">RM</span>
                  <input
                    type="number"
                    value={formData.cashback_amount}
                    onChange={(e) => setFormData({...formData, cashback_amount: e.target.value})}
                    className="flex-1 px-4 py-2.5 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                    placeholder="e.g., 500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Internal Commission (Internal Use Only) */}
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
            <h2 className="text-lg font-bold text-[#111827] mb-2">Internal Commission (Internal Use Only)</h2>
            <p className="text-xs text-[#6B7280] mb-4">These fields are strictly internal and will not be displayed on the public website.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">Unisave Commission (Local)</label>
                <div className="flex gap-2">
                  <span className="px-3 py-2.5 bg-[#F3F4F6] border border-[#E5E7EB] rounded-lg text-sm text-[#6B7280]">RM</span>
                  <input
                    type="number"
                    value={formData.unisave_commission_local || ''}
                    onChange={(e) => setFormData({...formData, unisave_commission_local: e.target.value})}
                    className="flex-1 px-4 py-2.5 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                    placeholder="e.g., 5000"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">Unisave Commission (International)</label>
                <div className="flex gap-2">
                  <span className="px-3 py-2.5 bg-[#F3F4F6] border border-[#E5E7EB] rounded-lg text-sm text-[#6B7280]">RM</span>
                  <input
                    type="number"
                    value={formData.unisave_commission_international || ''}
                    onChange={(e) => setFormData({...formData, unisave_commission_international: e.target.value})}
                    className="flex-1 px-4 py-2.5 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                    placeholder="e.g., 6000"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* APEL Entry */}
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
            <h2 className="text-lg font-bold text-[#111827] mb-4">Entry Requirements</h2>
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-2">APEL Entry</label>
              <select
                value={formData.apel_entry || ''}
                onChange={(e) => setFormData({...formData, apel_entry: e.target.value})}
                className="w-full px-4 py-2.5 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
              >
                <option value="">Select option</option>
                <option value="Accepted">Accepted</option>
                <option value="Not Accepted">Not Accepted</option>
              </select>
              <p className="text-xs text-[#6B7280] mt-1">This information can be displayed on the public program detail page.</p>
            </div>
          </div>

          {/* Eligibility Requirements */}
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-[#111827]">
                Eligibility & Entry Requirements
                <ReviewNeededBadge fieldName="eligibility_requirements" missingFields={missingFields} />
              </h2>
              <button
                type="button"
                onClick={addEligibilityRequirement}
                className="text-[#1E40AF] hover:text-[#1e3a8a] font-medium text-sm flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Requirement
              </button>
            </div>
            <div className="space-y-4">
              {formData.eligibility_requirements.map((req, index) => (
                <div key={index} className="border border-[#E5E7EB] rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-sm font-semibold text-[#374151]">Requirement {index + 1}</h3>
                    {formData.eligibility_requirements.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeEligibilityRequirement(index)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={req.title}
                      onChange={(e) => updateEligibilityRequirement(index, 'title', e.target.value)}
                      className="w-full px-4 py-2.5 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                      placeholder="Title (e.g., Academic Requirements)"
                    />
                    <textarea
                      value={req.description}
                      onChange={(e) => updateEligibilityRequirement(index, 'description', e.target.value)}
                      rows={3}
                      className="w-full px-4 py-2.5 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                      placeholder="Description"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* English Requirements */}
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
            <h2 className="text-lg font-bold text-[#111827] mb-4">English Requirements</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">IELTS</label>
                <input
                  type="text"
                  value={formData.english_requirements.ielts}
                  onChange={(e) => setFormData({
                    ...formData, 
                    english_requirements: {...formData.english_requirements, ielts: e.target.value}
                  })}
                  className="w-full px-4 py-2.5 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                  placeholder="e.g., 6.0 overall, no band below 5.5"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">TOEFL</label>
                <input
                  type="text"
                  value={formData.english_requirements.toefl}
                  onChange={(e) => setFormData({
                    ...formData, 
                    english_requirements: {...formData.english_requirements, toefl: e.target.value}
                  })}
                  className="w-full px-4 py-2.5 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                  placeholder="e.g., 80 iBT"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">MUET</label>
                <input
                  type="text"
                  value={formData.english_requirements.muet}
                  onChange={(e) => setFormData({
                    ...formData, 
                    english_requirements: {...formData.english_requirements, muet: e.target.value}
                  })}
                  className="w-full px-4 py-2.5 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                  placeholder="e.g., Band 4"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">Other</label>
                <input
                  type="text"
                  value={formData.english_requirements.other}
                  onChange={(e) => setFormData({
                    ...formData, 
                    english_requirements: {...formData.english_requirements, other: e.target.value}
                  })}
                  className="w-full px-4 py-2.5 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                  placeholder="Other accepted tests"
                />
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
            <h2 className="text-lg font-bold text-[#111827] mb-4">Call-to-Action Buttons</h2>
            <p className="text-sm text-[#6B7280] mb-4">Customize the button text and URLs shown on the program detail page.</p>
            <div className="space-y-4">
              {formData.cta_buttons.map((btn, index) => (
                <div key={index} className="border border-[#E5E7EB] rounded-lg p-4 bg-[#F9FAFB]">
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      btn.type === 'primary' ? 'bg-[#F97316] text-white' : 'bg-white border border-[#F97316] text-[#F97316]'
                    }`}>
                      {btn.type === 'primary' ? 'Primary' : 'Secondary'}
                    </span>
                    <span className="text-sm text-[#6B7280]">Button {index + 1}</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-[#6B7280] mb-1">Button Text</label>
                      <input
                        type="text"
                        value={btn.text}
                        onChange={(e) => updateCtaButton(index, 'text', e.target.value)}
                        className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent text-sm"
                        placeholder="Button label"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#6B7280] mb-1">URL (optional)</label>
                      <input
                        type="text"
                        value={btn.url}
                        onChange={(e) => updateCtaButton(index, 'url', e.target.value)}
                        className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent text-sm"
                        placeholder="https:// or leave empty for default action"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Ad Banners */}
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-[#111827]">Ad Banners</h2>
                <p className="text-sm text-[#6B7280]">Display promotional banners on the program detail page</p>
              </div>
              <button
                type="button"
                onClick={addAdBanner}
                className="text-[#1E40AF] hover:text-[#1e3a8a] font-medium text-sm flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Banner
              </button>
            </div>
            <div className="space-y-4">
              {formData.ad_banners.map((banner, index) => (
                <div key={index} className="border border-[#E5E7EB] rounded-lg p-4 bg-[#F9FAFB]">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm font-medium text-[#374151]">Banner {index + 1}</span>
                    {formData.ad_banners.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeAdBanner(index)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-[#6B7280] mb-1">Image URL</label>
                      <input
                        type="text"
                        value={banner.image_url}
                        onChange={(e) => updateAdBanner(index, 'image_url', e.target.value)}
                        className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent text-sm"
                        placeholder="https://example.com/banner.jpg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#6B7280] mb-1">Target URL (click destination)</label>
                      <input
                        type="text"
                        value={banner.target_url}
                        onChange={(e) => updateAdBanner(index, 'target_url', e.target.value)}
                        className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent text-sm"
                        placeholder="https://example.com/landing-page"
                      />
                    </div>
                  </div>
                  {banner.image_url && (
                    <div className="mt-3">
                      <img src={banner.image_url} alt="Banner preview" className="max-h-24 rounded border border-[#E5E7EB]" onError={(e) => e.target.style.display='none'} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Program Content - Rich Text */}
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
            <h2 className="text-lg font-bold text-[#111827] mb-4">Additional Content</h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">Program Modules (Rich Text)</label>
                <TipTapEditor
                  content={formData.program_modules}
                  onChange={(data) => setFormData({...formData, program_modules: data})}
                  placeholder="Additional details about program structure, semesters..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">Program Highlights</label>
                <TipTapEditor
                  content={formData.program_highlights}
                  onChange={(data) => setFormData({...formData, program_highlights: data})}
                  placeholder="Key highlights and features of the program..."
                />
              </div>
            </div>
          </div>

          {/* SEO Settings */}
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
            <SEOFields
              seoData={{ seo_title: formData.seo_title, seo_description: formData.seo_description }}
              onChange={(seoData) => setFormData({...formData, ...seoData})}
              missingFields={missingFields}
            />
          </div>

          {/* Publish Status */}
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="published"
                checked={formData.published}
                onChange={(e) => setFormData({...formData, published: e.target.checked})}
                className="w-5 h-5 text-[#1E40AF] border border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#1E40AF]"
              />
              <label htmlFor="published" className="text-sm font-medium text-[#374151] cursor-pointer">
                Publish (visible on website)
              </label>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-3 bg-[#1E40AF] hover:bg-[#1e3a8a] text-white font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : (isEdit ? 'Update' : 'Create')} Program
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/programs')}
              className="px-6 py-3 bg-white border border-[#E5E7EB] text-[#374151] hover:bg-[#F9FAFB] font-medium rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default ProgramForm;
